/**
 * Garde-fou SKU bijou — décision pure, sans dépendance React ni accès aux stores.
 *
 * Ce module est la SOURCE UNIQUE de la décision « quelles couleur/taille garder
 * pour construire un SKU ». Aucune règle de cette table ne doit être dupliquée
 * ailleurs (hooks, écrans) : ils doivent se contenter de résoudre les listes
 * disponibles puis d'appeler `decideSkuVariant`.
 *
 * Contexte (messika-pwa#7) : un SKU peut se retrouver avec une taille sur un
 * modèle qui n'en a pas (ex. `04186-WG-54`). Deux hypothèses restent ouvertes
 * côté UI (tarif pollué, ou instance de picker réutilisée) ; ce module ne les
 * tranche pas — il élague ou rejette en sortie, quelle que soit la cause.
 *
 * Table de décision (dans cet ordre) :
 *
 * | colors         | couleur demandée    | sizes      | taille demandée | Résultat                                                               |
 * |----------------|---------------------|------------|------------------|-------------------------------------------------------------------------|
 * | null           | quelconque          | —          | quelconque       | ok, valeurs inchangées, undetermined: true (tarif non chargé)          |
 * | [] (sans coul.)| fournie             | —          | quelconque       | ok, color: null, size: null, dropped: ['color','size']                 |
 * | []             | non fournie         | —          | fournie          | ok, size: null, dropped: ['size']                                      |
 * | non vide       | non fournie         | —          | quelconque       | reject: 'missing_color'                                                |
 * | non vide       | absente de la liste | —          | quelconque       | reject: 'unknown_color'                                                |
 * | non vide       | présente            | null/[]    | fournie          | ok, color conservée, size: null, dropped: ['size'] — cas du bug        |
 * | non vide       | présente            | non vide   | non fournie      | reject: 'missing_size'                                                 |
 * | non vide       | présente            | non vide   | absente          | reject: 'unknown_size'                                                 |
 * | non vide       | présente            | null/[]    | non fournie      | ok, rien à faire                                                       |
 * | non vide       | présente            | non vide   | présente         | ok, rien à faire                                                       |
 *
 * « non fournie » : `null`, `undefined`, chaîne vide après `trim()`, ou `'-'`.
 * Comparaison d'appartenance : égalité stricte après `trim()` des deux côtés,
 * sans normalisation de casse (les listes viennent du tarif, qui est la
 * référence). Les valeurs retournées dans `color`/`size` sont les valeurs
 * trimées conservées, ou `null`.
 */

export type VariantAvailability = {
   colors: string[] | null // null = indéterminé (tarif/modèle introuvable) ; [] = modèle sans couleur
   sizes: string[] | null // null = la couleur retenue n'a pas de taille ; [] traité comme null
}

export type SkuVariantDecision = {
   ok: boolean // false = la ligne doit être rejetée
   color: string | null // valeur à passer à makeSKU
   size: string | null // valeur à passer à makeSKU
   dropped: ('color' | 'size')[] // dimensions élaguées (vide si rien élagué)
   reject: null | 'missing_color' | 'unknown_color' | 'missing_size' | 'unknown_size'
   undetermined: boolean // true quand colors === null (on laisse passer tel quel)
}

/** `null`/`undefined`/vide-après-trim/`'-'` → non fourni. Renvoie la valeur trimée sinon. */
function normalizeProvided(value: string | null | undefined): string | null {
   if (value === null || value === undefined) {
      return null
   }
   const trimmed = value.trim()
   return trimmed === '' || trimmed === '-' ? null : trimmed
}

function rejectDecision(reject: NonNullable<SkuVariantDecision['reject']>): SkuVariantDecision {
   return { ok: false, color: null, size: null, dropped: [], reject, undetermined: false }
}

export function decideSkuVariant(color: string | null | undefined, size: string | null | undefined, avail: VariantAvailability): SkuVariantDecision {
   if (avail.colors === null) {
      // Tarif/modèle introuvable : on ne bloque jamais un ajout dans ce cas, valeurs inchangées.
      return {
         ok: true,
         color: color === undefined ? null : color,
         size: size === undefined ? null : size,
         dropped: [],
         reject: null,
         undetermined: true
      }
   }

   const providedColor = normalizeProvided(color)
   const providedSize = normalizeProvided(size)

   if (avail.colors.length === 0) {
      // Modèle sans couleur → structurellement sans taille non plus (cf. useTarifs.ts).
      const dropped: ('color' | 'size')[] = []
      if (providedColor !== null) {
         dropped.push('color')
      }
      if (providedSize !== null) {
         dropped.push('size')
      }
      return { ok: true, color: null, size: null, dropped, reject: null, undetermined: false }
   }

   if (providedColor === null) {
      return rejectDecision('missing_color')
   }

   const colorKnown = avail.colors.some((c) => c.trim() === providedColor)
   if (!colorKnown) {
      return rejectDecision('unknown_color')
   }

   const sizes = avail.sizes && avail.sizes.length > 0 ? avail.sizes : null

   if (sizes === null) {
      if (providedSize !== null) {
         return { ok: true, color: providedColor, size: null, dropped: ['size'], reject: null, undetermined: false }
      }
      return { ok: true, color: providedColor, size: null, dropped: [], reject: null, undetermined: false }
   }

   if (providedSize === null) {
      return rejectDecision('missing_size')
   }

   const sizeKnown = sizes.some((s) => s.trim() === providedSize)
   if (!sizeKnown) {
      return rejectDecision('unknown_size')
   }

   return { ok: true, color: providedColor, size: providedSize, dropped: [], reject: null, undetermined: false }
}
